import { useEffect, useState } from 'react';
import api from '../api/axios';
import PageTitle from '../components/PageTitle';

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('general'); // general, pricing, services, faqs, resources
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // General Settings & Pricing State
  const [settings, setSettings] = useState({
    contact_phone: '',
    support_email: '',
    instagram_handle: '',
    show_instagram: 'false',
    plan_1_title: '',
    plan_1_price: '',
    plan_1_desc: '',
    plan_1_features: '',
    plan_2_title: '',
    plan_2_price: '',
    plan_2_desc: '',
    plan_2_features: '',
    plan_3_title: '',
    plan_3_price: '',
    plan_3_desc: '',
    plan_3_features: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Tab 3: Services State & Forms
  const [services, setServices] = useState([]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    category: 'Registrations',
    type: 'OFFLINE',
    link: '',
    description: '',
    active: true,
    sortOrder: 0
  });
  const [savingService, setSavingService] = useState(false);
  const CATEGORIES = ['Income Tax & Audit', 'GST & Business', 'Registrations', 'Legal & Advisory'];
  const TYPES = ['ONLINE', 'OFFLINE'];

  // Tab 4: FAQs State & Forms
  const [faqs, setFaqs] = useState([]);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', active: true, sortOrder: 0 });
  const [savingFaq, setSavingFaq] = useState(false);

  // Tab 5: Resources States & Forms
  const [news, setNews] = useState([]);
  const [articles, setArticles] = useState([]);
  const [files, setFiles] = useState([]);
  const [newsForm, setNewsForm] = useState({ title: '', content: '' });
  const [articleForm, setArticleForm] = useState({ title: '', content: '', author: '' });
  const [fileForm, setFileForm] = useState({ title: '', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [newsSubmitting, setNewsSubmitting] = useState(false);
  const [articleSubmitting, setArticleSubmitting] = useState(false);
  const [fileSubmitting, setFileSubmitting] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [settingsRes, servicesRes, faqsRes, newsRes, articlesRes, filesRes] = await Promise.all([
        api.get('/api/settings/all').catch(() => ({ data: {} })),
        api.get('/api/app-services/all').catch(() => ({ data: [] })),
        api.get('/api/faq/all').catch(() => ({ data: [] })),
        api.get('/api/news/public').catch(() => ({ data: [] })),
        api.get('/api/articles/public').catch(() => ({ data: [] })),
        api.get('/api/admin-files/public').catch(() => ({ data: [] }))
      ]);

      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
      setServices(servicesRes.data);
      setFaqs(faqsRes.data);
      setNews(newsRes.data);
      setArticles(articlesRes.data);
      setFiles(filesRes.data);
    } catch (err) {
      console.error('Failed to load settings data', err);
      showMsg('error', 'Failed to load initial settings data.');
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  // --- General & Pricing Settings Save ---
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.post('/api/settings', settings);
      showMsg('success', 'Settings updated successfully!');
    } catch (err) {
      showMsg('error', 'Failed to update settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  // --- Services operations ---
  const openNewService = () => {
    setEditingService(null);
    setServiceForm({
      name: '',
      category: 'Registrations',
      type: 'OFFLINE',
      link: '',
      description: '',
      active: true,
      sortOrder: services.length + 1
    });
    setShowServiceForm(true);
  };

  const openEditService = svc => {
    setEditingService(svc);
    setServiceForm({
      name: svc.name,
      category: svc.category,
      type: svc.type,
      link: svc.link || '',
      description: svc.description || '',
      active: svc.active,
      sortOrder: svc.sortOrder
    });
    setShowServiceForm(true);
  };

  const saveService = async () => {
    if (!serviceForm.name.trim()) { alert('Service name is required'); return; }
    setSavingService(true);
    try {
      if (editingService) {
        const { data } = await api.put(`/api/app-services/${editingService.id}`, serviceForm);
        setServices(prev => prev.map(s => s.id === editingService.id ? data : s));
        showMsg('success', 'Service updated successfully');
      } else {
        const { data } = await api.post('/api/app-services', serviceForm);
        setServices(prev => [...prev, data]);
        showMsg('success', 'Service created successfully');
      }
      setShowServiceForm(false);
      setEditingService(null);
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Save service failed');
    } finally {
      setSavingService(false);
    }
  };

  const toggleServiceActive = async (svc) => {
    try {
      const { data } = await api.patch(`/api/app-services/${svc.id}/toggle`);
      setServices(prev => prev.map(s => s.id === svc.id ? data : s));
      showMsg('success', `Service "${data.name}" is now ${data.active ? 'Visible' : 'Hidden'}`);
    } catch {
      showMsg('error', 'Failed to toggle active status');
    }
  };

  const deleteService = async (svc) => {
    if (!window.confirm(`Are you sure you want to delete the service: "${svc.name}"?`)) return;
    try {
      await api.delete(`/api/app-services/${svc.id}`);
      setServices(prev => prev.filter(s => s.id !== svc.id));
      showMsg('success', 'Service deleted successfully');
    } catch {
      showMsg('error', 'Delete service failed');
    }
  };

  // --- FAQ operations ---
  const openNewFaq = () => {
    setEditingFaq(null);
    setFaqForm({ question: '', answer: '', active: true, sortOrder: faqs.length });
    setShowFaqForm(true);
  };

  const openEditFaq = faq => {
    setEditingFaq(faq);
    setFaqForm({ question: faq.question, answer: faq.answer, active: faq.active, sortOrder: faq.sortOrder });
    setShowFaqForm(true);
  };

  const saveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) { alert('Question and answer are required'); return; }
    setSavingFaq(true);
    try {
      if (editingFaq) {
        const { data } = await api.put(`/api/faq/${editingFaq.id}`, faqForm);
        setFaqs(prev => prev.map(f => f.id === editingFaq.id ? data : f));
        showMsg('success', 'FAQ updated');
      } else {
        const { data } = await api.post('/api/faq', faqForm);
        setFaqs(prev => [...prev, data]);
        showMsg('success', 'FAQ created');
      }
      setShowFaqForm(false);
      setEditingFaq(null);
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Save FAQ failed');
    } finally {
      setSavingFaq(false);
    }
  };

  const toggleFaq = async (faq) => {
    try {
      const { data } = await api.patch(`/api/faq/${faq.id}/toggle`);
      setFaqs(prev => prev.map(f => f.id === faq.id ? data : f));
      showMsg('success', `FAQ ${data.active ? 'published' : 'hidden'}`);
    } catch {
      showMsg('error', 'Failed to toggle FAQ');
    }
  };

  const deleteFaq = async (faq) => {
    if (!window.confirm(`Delete: "${faq.question.slice(0, 50)}..."?`)) return;
    try {
      await api.delete(`/api/faq/${faq.id}`);
      setFaqs(prev => prev.filter(f => f.id !== faq.id));
      showMsg('success', 'FAQ deleted');
    } catch {
      showMsg('error', 'Delete FAQ failed');
    }
  };

  // --- Resources operations ---
  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    if (!newsForm.title || !newsForm.content) return;
    setNewsSubmitting(true);
    try {
      await api.post('/api/news', newsForm);
      setNewsForm({ title: '', content: '' });
      showMsg('success', 'News published successfully!');
      // Reload news list
      const r = await api.get('/api/news/public');
      setNews(r.data);
    } catch (err) {
      showMsg('error', 'Failed to publish news.');
    } finally {
      setNewsSubmitting(false);
    }
  };

  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    if (!articleForm.title || !articleForm.content) return;
    setArticleSubmitting(true);
    try {
      await api.post('/api/articles', articleForm);
      setArticleForm({ title: '', content: '', author: '' });
      showMsg('success', 'Article published successfully!');
      // Reload articles
      const r = await api.get('/api/articles/public');
      setArticles(r.data);
    } catch (err) {
      showMsg('error', 'Failed to publish article.');
    } finally {
      setArticleSubmitting(false);
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!fileForm.title || !fileForm.description || !selectedFile) {
      alert('Please fill in all fields and select a file.');
      return;
    }
    setFileSubmitting(true);
    const formData = new FormData();
    formData.append('title', fileForm.title);
    formData.append('description', fileForm.description);
    formData.append('file', selectedFile);

    try {
      await api.post('/api/admin-files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFileForm({ title: '', description: '' });
      setSelectedFile(null);
      const fileInput = document.getElementById('file-upload-input');
      if (fileInput) fileInput.value = '';
      showMsg('success', 'File uploaded successfully!');
      // Reload files
      const r = await api.get('/api/admin-files/public');
      setFiles(r.data);
    } catch (err) {
      showMsg('error', 'Failed to upload file.');
    } finally {
      setFileSubmitting(false);
    }
  };

  const handleDeleteNews = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news item?')) return;
    try {
      await api.delete(`/api/news/${id}`);
      setNews(prev => prev.filter(item => item.id !== id));
      showMsg('success', 'News deleted');
    } catch (err) {
      showMsg('error', 'Failed to delete news.');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await api.delete(`/api/articles/${id}`);
      setArticles(prev => prev.filter(item => item.id !== id));
      showMsg('success', 'Article deleted');
    } catch (err) {
      showMsg('error', 'Failed to delete article.');
    }
  };

  const handleDeleteFile = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await api.delete(`/api/admin-files/${id}`);
      setFiles(prev => prev.filter(item => item.id !== id));
      showMsg('success', 'File deleted');
    } catch (err) {
      showMsg('error', 'Failed to delete file.');
    }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="Admin Settings Hub" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Control Panel</div>
        <h1>Settings Hub</h1>
        <p>Unifies brand configs, plans pricing, services offerings, homepage FAQs and utility resources.</p>
      </div>

      <div className="page">
        {msg && (
          <div className={`alert alert-${msg.type}`} style={{ marginBottom: '1.5rem' }}>
            {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
          </div>
        )}

        {/* Tab Selection Row */}
        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '2px solid var(--creamd)', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {[
            { id: 'general', label: '⚙️ General' },
            { id: 'pricing', label: '💰 ITR Pricing' },
            { id: 'services', label: '🛠️ Services' },
            { id: 'faqs', label: '❓ FAQs' },
            { id: 'resources', label: '📢 Resources & Utilities' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: activeTab === tab.id ? 'var(--navy)' : 'transparent',
                color: activeTab === tab.id ? '#fff' : 'var(--text)',
                border: 'none',
                padding: '0.85rem 1.25rem',
                fontSize: '.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                transition: 'all 0.2s',
                marginBottom: '-2px',
                borderBottom: activeTab === tab.id ? '2px solid var(--gold)' : 'none'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB 1: GENERAL SETTINGS */}
        {activeTab === 'general' && (
          <form onSubmit={handleSettingsSubmit} className="card" style={{ borderTop: '4px solid var(--navy)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '1.5rem', color: 'var(--navy)' }}>General Contact Details & Settings</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
              <div className="field">
                <label>Contact Phone Number *</label>
                <input
                  type="text"
                  value={settings.contact_phone}
                  onChange={e => setSettings({ ...settings, contact_phone: e.target.value })}
                  placeholder="e.g. +91 9411777195"
                  required
                />
              </div>

              <div className="field">
                <label>Support Email *</label>
                <input
                  type="email"
                  value={settings.support_email}
                  onChange={e => setSettings({ ...settings, support_email: e.target.value })}
                  placeholder="e.g. info@mytaxniti.com"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
              <div className="field">
                <label>Instagram Handle</label>
                <input
                  type="text"
                  value={settings.instagram_handle}
                  onChange={e => setSettings({ ...settings, instagram_handle: e.target.value })}
                  placeholder="e.g. mytaxniti"
                />
              </div>

              <div className="field">
                <label>Instagram Handle Visibility</label>
                <select
                  value={settings.show_instagram}
                  onChange={e => setSettings({ ...settings, show_instagram: e.target.value })}
                  style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '0.65rem' }}
                >
                  <option value="true">Show Instagram Handle in Footer</option>
                  <option value="false">Hide Instagram Handle</option>
                </select>
              </div>
            </div>

            <button type="submit" className="btn btn-gold" disabled={savingSettings}>
              {savingSettings ? 'Saving...' : '💾 Save General Configs'}
            </button>
          </form>
        )}

        {/* TAB 2: PRICING PLANS */}
        {activeTab === 'pricing' && (
          <form onSubmit={handleSettingsSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* PLAN 1 */}
            <div className="card" style={{ borderTop: '4px solid var(--gold)' }}>
              <h3 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '1rem', color: 'var(--navy)' }}>Plan 1: Basic Plan</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="field">
                  <label>Plan Title</label>
                  <input
                    type="text"
                    value={settings.plan_1_title}
                    onChange={e => setSettings({ ...settings, plan_1_title: e.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label>Plan Price (₹)</label>
                  <input
                    type="text"
                    value={settings.plan_1_price}
                    onChange={e => setSettings({ ...settings, plan_1_price: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>Plan Description</label>
                <input
                  type="text"
                  value={settings.plan_1_desc}
                  onChange={e => setSettings({ ...settings, plan_1_desc: e.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label>Plan Features (one feature per line)</label>
                <textarea
                  rows={4}
                  value={settings.plan_1_features}
                  onChange={e => setSettings({ ...settings, plan_1_features: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* PLAN 2 */}
            <div className="card" style={{ borderTop: '4px solid var(--navy)' }}>
              <h3 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '1rem', color: 'var(--navy)' }}>Plan 2: Professional Plan (Featured)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="field">
                  <label>Plan Title</label>
                  <input
                    type="text"
                    value={settings.plan_2_title}
                    onChange={e => setSettings({ ...settings, plan_2_title: e.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label>Plan Price (₹)</label>
                  <input
                    type="text"
                    value={settings.plan_2_price}
                    onChange={e => setSettings({ ...settings, plan_2_price: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>Plan Description</label>
                <input
                  type="text"
                  value={settings.plan_2_desc}
                  onChange={e => setSettings({ ...settings, plan_2_desc: e.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label>Plan Features (one feature per line)</label>
                <textarea
                  rows={4}
                  value={settings.plan_2_features}
                  onChange={e => setSettings({ ...settings, plan_2_features: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* PLAN 3 */}
            <div className="card" style={{ borderTop: '4px solid var(--gold)' }}>
              <h3 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '1rem', color: 'var(--navy)' }}>Plan 3: NRI Premium Plan</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="field">
                  <label>Plan Title</label>
                  <input
                    type="text"
                    value={settings.plan_3_title}
                    onChange={e => setSettings({ ...settings, plan_3_title: e.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label>Plan Price (₹)</label>
                  <input
                    type="text"
                    value={settings.plan_3_price}
                    onChange={e => setSettings({ ...settings, plan_3_price: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>Plan Description</label>
                <input
                  type="text"
                  value={settings.plan_3_desc}
                  onChange={e => setSettings({ ...settings, plan_3_desc: e.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label>Plan Features (one feature per line)</label>
                <textarea
                  rows={4}
                  value={settings.plan_3_features}
                  onChange={e => setSettings({ ...settings, plan_3_features: e.target.value })}
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn btn-gold" disabled={savingSettings} style={{ alignSelf: 'flex-start' }}>
              {savingSettings ? 'Saving plans...' : '💾 Save ITR Pricing Details'}
            </button>
          </form>
        )}

        {/* TAB 3: SERVICES */}
        {activeTab === 'services' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '.85rem', color: 'var(--muted)' }}>
                Configure services shown in the Navbar dropdown. Users will be prompted for callbacks on offline services.
              </div>
              <button className="btn btn-primary" onClick={openNewService}>+ Add New Service</button>
            </div>

            {/* Service Form Modal */}
            {showServiceForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(7, 15, 30, 0.6)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
              }}>
                <div className="card" style={{
                  width: '100%',
                  maxWidth: '600px',
                  border: '2px solid var(--gold)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: '1.1rem' }}>
                      {editingService ? '✏️ Edit Service' : '➕ New Service'}
                    </div>
                    <button onClick={() => { setShowServiceForm(false); setEditingService(null); }} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <div className="field" style={{ flex: 2, minWidth: 200 }}>
                      <label>Service Name<span className="req">*</span></label>
                      <input value={serviceForm.name} onChange={e => setServiceForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. MSME Registration" />
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 150 }}>
                      <label>Category</label>
                      <select value={serviceForm.category} onChange={e => setServiceForm(f => ({ ...f, category: e.target.value }))}
                        style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '0.65rem' }}>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <div className="field" style={{ flex: 1, minWidth: 150 }}>
                      <label>Service Type</label>
                      <select value={serviceForm.type} onChange={e => setServiceForm(f => ({ ...f, type: e.target.value }))}
                        style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '0.65rem' }}>
                        {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {serviceForm.type === 'ONLINE' && (
                      <div className="field" style={{ flex: 2, minWidth: 200 }}>
                        <label>Frontend Link/Route</label>
                        <input value={serviceForm.link} onChange={e => setServiceForm(f => ({ ...f, link: e.target.value }))}
                          placeholder="e.g. /file, /gst, /gst-return" />
                      </div>
                    )}
                  </div>

                  <div className="field" style={{ marginBottom: '1.25rem' }}>
                    <label>Description / Brief Understanding (shown to users before requesting callback)</label>
                    <textarea rows={3} value={serviceForm.description} onChange={e => setServiceForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Micro, Small and Medium Enterprises registration provides government benefits, subsidy schemes..." />
                  </div>

                  <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.25rem', alignItems: 'center' }}>
                    <div className="field" style={{ flex: 1 }}>
                      <label>Sort Order</label>
                      <input type="number" min={0} value={serviceForm.sortOrder}
                        onChange={e => setServiceForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                        style={{ width: 100 }} />
                      <span className="hint">Lower number = shown first</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '1.2rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', fontSize: '.88rem', fontWeight: 500 }}>
                        <input type="checkbox" checked={serviceForm.active}
                          onChange={e => setServiceForm(f => ({ ...f, active: e.target.checked }))}
                          style={{ width: 16, height: 16, cursor: 'pointer' }} />
                        Active / Display in Dropdown
                      </label>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '.75rem' }}>
                    <button className="btn btn-gold" onClick={saveService} disabled={savingService}>
                      {savingService ? 'Saving...' : editingService ? '💾 Save Changes' : '✅ Create Service'}
                    </button>
                    <button className="btn btn-outline" onClick={() => { setShowServiceForm(false); setEditingService(null); }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Services List Table */}
            {services.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚙️</div>
                <p>No services found.</p>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.88rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--navy)', color: '#fff', borderBottom: '2px solid var(--gold)' }}>
                      <th style={{ padding: '0.9rem 1rem', textAlign: 'left' }}>Order</th>
                      <th style={{ padding: '0.9rem 1rem', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '0.9rem 1rem', textAlign: 'left' }}>Category</th>
                      <th style={{ padding: '0.9rem 1rem', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '0.9rem 1rem', textAlign: 'left' }}>Action Config</th>
                      <th style={{ padding: '0.9rem 1rem', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '0.9rem 1rem', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((svc) => (
                      <tr key={svc.id} style={{ borderBottom: '1px solid var(--border)', background: svc.active ? 'transparent' : 'rgba(7,15,30,0.02)' }}>
                        <td style={{ padding: '0.9rem 1rem', fontWeight: 600 }}>{svc.sortOrder}</td>
                        <td style={{ padding: '0.9rem 1rem', fontWeight: 700, color: 'var(--navy)' }}>{svc.name}</td>
                        <td style={{ padding: '0.9rem 1rem' }}><span className="badge badge-info">{svc.category}</span></td>
                        <td style={{ padding: '0.9rem 1rem' }}>
                          <span style={{
                            background: svc.type === 'ONLINE' ? 'var(--greenl)' : 'var(--bluel)',
                            color: svc.type === 'ONLINE' ? 'var(--green)' : 'var(--blue)',
                            padding: '0.15rem 0.5rem',
                            borderRadius: 4,
                            fontSize: '.68rem',
                            fontWeight: 700
                          }}>
                            {svc.type}
                          </span>
                        </td>
                        <td style={{ padding: '0.9rem 1rem', color: 'var(--muted)', fontSize: '.78rem' }}>
                          {svc.type === 'ONLINE' ? `Link: ${svc.link}` : `Callback prompt`}
                        </td>
                        <td style={{ padding: '0.9rem 1rem' }}>
                          <button onClick={() => toggleServiceActive(svc)}
                            style={{
                              background: svc.active ? 'var(--greenl)' : 'var(--creamd)',
                              color: svc.active ? 'var(--green)' : 'var(--muted)',
                              border: 'none',
                              borderRadius: 7,
                              padding: '.35rem .75rem',
                              fontSize: '.75rem',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}>
                            {svc.active ? '👁 Active' : '🚫 Hidden'}
                          </button>
                        </td>
                        <td style={{ padding: '0.9rem 1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '.4rem', justifyContent: 'center' }}>
                            <button className="btn btn-outline" style={{ padding: '0.35rem 0.65rem', fontSize: '.72rem' }} onClick={() => openEditService(svc)}>
                              ✏️ Edit
                            </button>
                            <button className="btn btn-outline" style={{ padding: '0.35rem 0.65rem', fontSize: '.72rem', borderColor: 'var(--red)', color: 'var(--red)' }} onClick={() => deleteService(svc)}>
                              🗑 Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: FAQs */}
        {activeTab === 'faqs' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '.85rem', color: 'var(--muted)' }}>
                FAQs shown on the public homepage. Toggle to hide/show without deleting.
              </div>
              <button className="btn btn-primary" onClick={openNewFaq}>+ Add New FAQ</button>
            </div>

            {/* FAQ Form */}
            {showFaqForm && (
              <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid var(--gold)' }}>
                <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: '1.25rem', fontSize: '1rem' }}>
                  {editingFaq ? '✏️ Edit FAQ' : '➕ New FAQ'}
                </div>

                <div className="field" style={{ marginBottom: '1rem' }}>
                  <label>Question *</label>
                  <input value={faqForm.question} onChange={e => setFaqForm({ ...faqForm, question: e.target.value })}
                    placeholder="e.g. What documents do I need for ITR filing?" />
                </div>

                <div className="field" style={{ marginBottom: '1rem' }}>
                  <label>Answer *</label>
                  <textarea rows={4} value={faqForm.answer} onChange={e => setFaqForm({ ...faqForm, answer: e.target.value })}
                    placeholder="Write the answer here..." />
                </div>

                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.25rem', alignItems: 'center' }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label>Sort Order</label>
                    <input type="number" min={0} value={faqForm.sortOrder}
                      onChange={e => setFaqForm({ ...faqForm, sortOrder: parseInt(e.target.value) || 0 })}
                      style={{ width: 100 }} />
                    <span className="hint">Lower number = shown first</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '1.2rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', fontSize: '.88rem', fontWeight: 500 }}>
                      <input type="checkbox" checked={faqForm.active}
                        onChange={e => setFaqForm({ ...faqForm, active: e.target.checked })}
                        style={{ width: 16, height: 16, cursor: 'pointer' }} />
                      Publish on homepage
                    </label>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '.75rem' }}>
                  <button className="btn btn-gold" onClick={saveFaq} disabled={savingFaq}>
                    {savingFaq ? 'Saving...' : editingFaq ? '💾 Save Changes' : '✅ Create FAQ'}
                  </button>
                  <button className="btn btn-outline" onClick={() => { setShowFaqForm(false); setEditingFaq(null); }}>Cancel</button>
                </div>
              </div>
            )}

            {/* FAQs List */}
            {faqs.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>❓</div>
                <p>No FAQs yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
                {faqs.map((faq) => (
                  <div key={faq.id} className="card card-sm"
                    style={{ border: `1px solid ${faq.active ? 'var(--border)' : 'var(--creamd)'}`, opacity: faq.active ? 1 : .65 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.4rem' }}>
                          <span style={{ background: 'var(--creamd)', color: 'var(--muted)', fontSize: '.7rem', fontWeight: 700, padding: '.15rem .5rem', borderRadius: 4, minWidth: 28, textAlign: 'center' }}>
                            #{faq.sortOrder}
                          </span>
                          <span style={{ fontWeight: 600, fontSize: '.92rem', color: 'var(--navy)' }}>{faq.question}</span>
                          {!faq.active && (
                            <span style={{ background: 'var(--creamd)', color: 'var(--muted)', fontSize: '.68rem', fontWeight: 600, padding: '.15rem .5rem', borderRadius: 4 }}>
                              Hidden
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: '.83rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>
                          {faq.answer.length > 140 ? faq.answer.slice(0, 140) + '...' : faq.answer}
                        </p>
                      </div>

                      <div style={{ display: 'flex', gap: '.5rem', flexShrink: 0 }}>
                        <button onClick={() => toggleFaq(faq)}
                          style={{ background: faq.active ? 'var(--greenl)' : 'var(--creamd)', color: faq.active ? 'var(--green)' : 'var(--muted)', border: 'none', borderRadius: 7, padding: '.35rem .75rem', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' }}>
                          {faq.active ? '👁 Published' : '🚫 Hidden'}
                        </button>
                        <button onClick={() => openEditFaq(faq)}
                          style={{ background: 'var(--bluel)', color: 'var(--blue)', border: 'none', borderRadius: 7, padding: '.35rem .75rem', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' }}>
                          ✏️ Edit
                        </button>
                        <button onClick={() => deleteFaq(faq)}
                          style={{ background: 'var(--redl)', color: 'var(--red)', border: 'none', borderRadius: 7, padding: '.35rem .75rem', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' }}>
                          🗑
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 5: RESOURCES */}
        {activeTab === 'resources' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2.5rem' }}>
            
            {/* News alerts */}
            <section className="card" style={{ borderTop: '4px solid var(--navy)' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '1.5rem', color: 'var(--navy)' }}>📢 Tax News & Alerts</h2>
              <form onSubmit={handleNewsSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                <div className="field">
                  <label>News Title</label>
                  <input type="text" placeholder="e.g. Due date extended for ITR filing" value={newsForm.title} onChange={e => setNewsForm({ ...newsForm, title: e.target.value })} required />
                </div>
                <div className="field">
                  <label>News Content</label>
                  <textarea rows={4} placeholder="Provide news details here..." value={newsForm.content} onChange={e => setNewsForm({ ...newsForm, content: e.target.value })} required />
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={newsSubmitting} style={{ alignSelf: 'flex-start' }}>
                  {newsSubmitting ? 'Publishing...' : '📢 Publish News Alert'}
                </button>
              </form>

              <h3 style={{ fontSize: '1rem', marginBottom: '1rem', borderBottom: '1px solid var(--creamd)', paddingBottom: '0.5rem' }}>Existing News</h3>
              {news.length === 0 ? <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>No news alerts published.</p> : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Date</th>
                        <th style={{ width: '100px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {news.map(item => (
                        <tr key={item.id}>
                          <td style={{ fontWeight: 600 }}>{item.title}</td>
                          <td>{new Date(item.publishedAt).toLocaleDateString('en-IN')}</td>
                          <td style={{ textAlign: 'center' }}>
                            <button onClick={() => handleDeleteNews(item.id)} className="btn btn-danger btn-sm" style={{ padding: '0.25rem 0.5rem', fontSize: '0.72rem' }}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Articles & Guides */}
            <section className="card" style={{ borderTop: '4px solid var(--navy)' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '1.5rem', color: 'var(--navy)' }}>✍️ In-depth Articles & Guides</h2>
              <form onSubmit={handleArticleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                <div className="form-grid">
                  <div className="field">
                    <label>Article Title</label>
                    <input type="text" placeholder="e.g. Tax planning under Section 80C" value={articleForm.title} onChange={e => setArticleForm({ ...articleForm, title: e.target.value })} required />
                  </div>
                  <div className="field">
                    <label>Author Name</label>
                    <input type="text" placeholder="e.g. CA Prabhat Dwivedi" value={articleForm.author} onChange={e => setArticleForm({ ...articleForm, author: e.target.value })} />
                  </div>
                </div>
                <div className="field">
                  <label>Article Content</label>
                  <textarea rows={6} placeholder="Write full article here..." value={articleForm.content} onChange={e => setArticleForm({ ...articleForm, content: e.target.value })} required />
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={articleSubmitting} style={{ alignSelf: 'flex-start' }}>
                  {articleSubmitting ? 'Publishing...' : '✍️ Publish Article'}
                </button>
              </form>

              <h3 style={{ fontSize: '1rem', marginBottom: '1rem', borderBottom: '1px solid var(--creamd)', paddingBottom: '0.5rem' }}>Existing Articles</h3>
              {articles.length === 0 ? <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>No articles published.</p> : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Author</th>
                        <th>Date</th>
                        <th style={{ width: '100px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.map(item => (
                        <tr key={item.id}>
                          <td style={{ fontWeight: 600 }}>{item.title}</td>
                          <td>{item.author}</td>
                          <td>{new Date(item.publishedAt).toLocaleDateString('en-IN')}</td>
                          <td style={{ textAlign: 'center' }}>
                            <button onClick={() => handleDeleteArticle(item.id)} className="btn btn-danger btn-sm" style={{ padding: '0.25rem 0.5rem', fontSize: '0.72rem' }}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Utility files */}
            <section className="card" style={{ borderTop: '4px solid var(--navy)' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '1.5rem', color: 'var(--navy)' }}>📁 Convenience Files & Utilities</h2>
              <form onSubmit={handleFileSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                <div className="form-grid">
                  <div className="field">
                    <label>Resource Title</label>
                    <input type="text" placeholder="e.g. FY 2025-26 Excel Tax Calculator" value={fileForm.title} onChange={e => setFileForm({ ...fileForm, title: e.target.value })} required />
                  </div>
                  <div className="field">
                    <label>Resource Description</label>
                    <input type="text" placeholder="Briefly describe what this file does..." value={fileForm.description} onChange={e => setFileForm({ ...fileForm, description: e.target.value })} required />
                  </div>
                </div>
                <div className="field">
                  <label>Select File</label>
                  <div className="file-zone" style={{ padding: '1.5rem' }}>
                    <input type="file" id="file-upload-input" onChange={e => setSelectedFile(e.target.files[0])} required />
                    <div className="file-zone-label">
                      {selectedFile ? '✅ Selected file: ' : '📁 Click or Drag to upload file'}
                    </div>
                    {selectedFile && (
                      <div className="file-name" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </div>
                    )}
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={fileSubmitting} style={{ alignSelf: 'flex-start' }}>
                  {fileSubmitting ? 'Uploading...' : '📁 Upload Utility File'}
                </button>
              </form>

              <h3 style={{ fontSize: '1rem', marginBottom: '1rem', borderBottom: '1px solid var(--creamd)', paddingBottom: '0.5rem' }}>Uploaded Utilities</h3>
              {files.length === 0 ? <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>No utilities uploaded.</p> : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Uploaded On</th>
                        <th style={{ width: '100px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map(item => (
                        <tr key={item.id}>
                          <td style={{ fontWeight: 600 }}>{item.title}</td>
                          <td>{item.description}</td>
                          <td>{new Date(item.uploadedAt).toLocaleDateString('en-IN')}</td>
                          <td style={{ textAlign: 'center' }}>
                            <button onClick={() => handleDeleteFile(item.id)} className="btn btn-danger btn-sm" style={{ padding: '0.25rem 0.5rem', fontSize: '0.72rem' }}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </>
  );
}
